import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  Stethoscope,
  ClipboardList,
  Activity,
  Calendar,
  Clock,
  ArrowRight,
  Plus,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Dynamic greeting based on time
const getGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return t('greetings.morning');
  if (hour >= 12 && hour < 19) return t('greetings.afternoon');
  return t('greetings.evening');
};

// Get current shift with translation
const getCurrentShift = (t) => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 14) return t('shifts.morning');
  if (hour >= 14 && hour < 22) return t('shifts.afternoon');
  return t('shifts.night');
};

// Mini bar chart data (simulated for visual effect)
const generateBars = (seed = 0) => {
  const heights = [45, 60, 35, 80, 55, 70, 90];
  return heights.map((h, i) => ({
    height: h + (seed * 5) % 30,
    active: i >= 3
  }));
};

const HeroCard = ({ userName, activePatients, t, i18n }) => {
  const formatDate = () => {
    const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
    return new Date().toLocaleDateString(locale, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="hero-card relative z-10">
      <span className="hero-eyebrow">
        {t('operationsSummary')}
      </span>
      
      <h1 className="hero-title mt-4">
        {getGreeting(t)}, {userName || 'Usuario'}. <strong>{activePatients} {t('patientsLabel')}</strong> {t('underCare')}.
      </h1>
      
      <p className="hero-sub">
        {t('subtitle')}
      </p>
      
      <div className="hero-meta">
        <div className="hero-meta-item">
          <span className="hero-meta-label">{t('dateLabel')}</span>
          <span className="hero-meta-value">{formatDate()}</span>
        </div>
        <div className="hero-meta-item">
          <span className="hero-meta-label">{t('shiftLabel')}</span>
          <span className="hero-meta-value">{getCurrentShift(t)}</span>
        </div>
      </div>
    </div>
  );
};

const QuickActionsCard = ({ navigate, t }) => (
  <div className="side-card h-full">
    <h3>{t('quickActions.title', 'Acciones Rápidas')}</h3>
    <div className="space-y-3">
      <button 
        className="btn-primary"
        onClick={() => navigate('/orders')}
        data-testid="quick-new-order"
      >
        <Plus className="mr-2 h-4 w-4 inline" />
        {t('quickActions.newOrder', 'Nueva Orden de Servicio')}
      </button>
      <button 
        className="btn-ghost"
        onClick={() => navigate('/patients')}
        data-testid="quick-new-patient"
      >
        <Plus className="mr-2 h-4 w-4" />
        {t('quickActions.registerPatient', 'Registrar Paciente')}
      </button>
      <button 
        className="btn-ghost"
        onClick={() => navigate('/orders')}
        data-testid="quick-view-orders"
      >
        <ClipboardList className="mr-2 h-4 w-4" />
        {t('quickActions.viewAllOrders', 'Ver Todas las Órdenes')}
      </button>
    </div>
  </div>
);

const MetricCard = ({ title, value, icon: Icon, subtext, colorClass, accentValue, bars }) => (
  <div className={`metric-card ${colorClass}`}>
    <div className="metric-header">
      <span className="metric-label">{title}</span>
      <div className={`metric-icon ${colorClass.replace('c', '')}`}>
        <Icon />
      </div>
    </div>
    <div className={`metric-value ${accentValue ? 'accent' : ''}`}>{value}</div>
    <div className="metric-sub">{subtext}</div>
    {bars && (
      <div className="metric-chart">
        {bars.map((bar, i) => (
          <div 
            key={i} 
            className={`bar ${bar.active ? 'on' : ''}`}
            style={{ height: `${bar.height}%` }}
          />
        ))}
      </div>
    )}
  </div>
);

const RecentOrdersTable = ({ orders, navigate, t }) => {
  const getStatusBadge = (status) => {
    if (status === 'in_progress') return 'badge-normal';
    if (status === 'completed') return 'badge-low';
    if (status === 'pending' || status === 'pending_assignment') return 'badge-high';
    return 'badge-normal';
  };

  const getPriorityBars = (priority) => {
    if (priority === 'urgent' || priority === 'high') return 'high';
    if (priority === 'normal') return 'medium';
    return 'low';
  };

  return (
    <div className="side-card col-span-1 lg:col-span-2" style={{ padding: 0 }}>
      <div className="card-head" style={{ padding: 'var(--s-6)', paddingBottom: 'var(--s-4)', marginBottom: 0 }}>
        <h3 style={{ marginBottom: 0 }}>{t('recentOrders.title', 'Órdenes Recientes')}</h3>
        <button className="link-pill" onClick={() => navigate('/orders')}>
          {t('recentOrders.viewAll', 'Ver todas')} <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      
      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <ClipboardList />
          </div>
          <div className="empty-state-title">{t('recentOrders.noOrders', 'No hay órdenes recientes')}</div>
          <div className="empty-state-hint">{t('recentOrders.hint', 'Las órdenes nuevas aparecerán aquí')}</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full" data-testid="recent-orders-table">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-[0.1em]">
                  {t('recentOrders.patient', 'Paciente')}
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-[0.1em]">
                  {t('recentOrders.service', 'Servicio')}
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-[0.1em]">
                  {t('recentOrders.status', 'Estado')}
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-[0.1em]">
                  {t('recentOrders.priority', 'Prioridad')}
                </th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 5).map((order, idx) => (
                <tr key={idx} className="hover:bg-[var(--bg-soft)]" style={{ boxShadow: 'inset 0 1px 0 var(--line-soft)' }}>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="patient-avatar">
                        {order.patient_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <span className="text-[13px] font-medium text-[var(--ink)]">{order.patient_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-[13px] text-[var(--ink-2)]">
                    {order.services?.[0] || 'N/A'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className={`badge ${getStatusBadge(order.status)}`}>
                      {t(`orderStatus.${order.status}`, order.status)}
                    </span>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className={`priority-bars ${getPriorityBars(order.priority)}`}>
                      <div className="bar"></div>
                      <div className="bar"></div>
                      <div className="bar"></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const OrderStatusCard = ({ ordersByStatus, t }) => {
  const statusKeys = {
    received: 'orderStatus.received',
    pending_assignment: 'orderStatus.pending_assignment',
    assigned: 'orderStatus.assigned',
    in_progress: 'orderStatus.in_progress',
    completed: 'orderStatus.completed',
    cancelled: 'orderStatus.cancelled'
  };

  const hasData = ordersByStatus && Object.values(ordersByStatus).some(v => v > 0);

  return (
    <div className="side-card">
      <h3>{t('orderStatus.title', 'Estado de Órdenes')}</h3>
      
      {!hasData ? (
        <div className="empty-state" style={{ padding: 'var(--s-6) 0' }}>
          <div className="empty-state-icon">
            <Clock />
          </div>
          <div className="empty-state-title text-[13px]">{t('orderStatus.empty', 'Sin órdenes activas')}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(ordersByStatus || {}).map(([status, count]) => (
            <div key={status} className="flex items-center justify-between py-2" style={{ boxShadow: 'inset 0 -1px 0 var(--line-soft)' }}>
              <span className="text-[13px] text-[var(--ink-2)]">
                {t(statusKeys[status] || status, status)}
              </span>
              <span className="text-[13px] font-semibold text-[var(--ink)]">
                {count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('dashboard');

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`),
        axios.get(`${API_URL}/api/orders?limit=5`)
      ]);
      
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--red)]"></div>
      </div>
    );
  }

  const userName = user ? `${user.first_name}` : '';

  return (
    <div className="space-y-6 p-2" data-testid="dashboard-page">
      {/* Hero + Quick Actions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <HeroCard 
            userName={userName}
            activePatients={stats?.total_patients || 0}
            t={t}
            i18n={i18n}
          />
        </div>
        <QuickActionsCard navigate={navigate} t={t} />
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('stats.activePatients', 'PACIENTES ACTIVOS')}
          value={stats?.total_patients || 0}
          icon={Users}
          subtext={t('stats.inHomeCare', 'En atención domiciliaria')}
          colorClass="c1"
          accentValue={true}
          bars={generateBars(1)}
        />
        <MetricCard
          title={t('stats.availableSpecialists', 'ESPECIALISTAS DISPONIBLES')}
          value={stats?.available_specialists || 0}
          icon={Stethoscope}
          subtext={t('stats.readyToAssign', 'Listos para asignar')}
          colorClass="c2"
          bars={generateBars(2)}
        />
        <MetricCard
          title={t('stats.activeHospitalizations', 'HOSPITALIZACIONES ACTIVAS')}
          value={stats?.active_hospitalizations || 0}
          icon={Activity}
          subtext={t('stats.inProgress', 'En progreso')}
          colorClass="c3"
          bars={generateBars(3)}
        />
        <MetricCard
          title={t('stats.todayOrders', 'ÓRDENES DEL DÍA')}
          value={stats?.todays_orders || 0}
          icon={ClipboardList}
          subtext={`${stats?.completed_today || 0} ${t('stats.completed', 'completadas')}`}
          colorClass="c4"
          bars={generateBars(4)}
        />
      </div>

      {/* Recent Orders + Status Summary Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RecentOrdersTable orders={recentOrders} navigate={navigate} t={t} />
        <OrderStatusCard ordersByStatus={stats?.orders_by_status} t={t} />
      </div>
    </div>
  );
};

export default DashboardPage;
